import { supabase } from '@/lib/supabase';

const DEVICE_ID_KEY = 'karebe-pwa-device-id';

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export interface PushSubscriptionResult {
  permission: NotificationPermission | 'unsupported';
  subscription: PushSubscription | null;
  deviceId: string;
}

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

export function getOrCreatePwaDeviceId() {
  const existing = localStorage.getItem(DEVICE_ID_KEY);
  if (existing) {
    return existing;
  }

  const next = crypto.randomUUID();
  localStorage.setItem(DEVICE_ID_KEY, next);
  return next;
}

export async function registerPwaServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js');
    return registration;
  } catch (error) {
    console.error('[PWA] Service worker registration failed:', error);
    return null;
  }
}

export function canUseNotifications() {
  return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
}

export async function requestNotificationPermission() {
  if (!canUseNotifications()) {
    return 'unsupported' as const;
  }

  return Notification.requestPermission();
}

async function getVapidPublicKey() {
  if (import.meta.env.VITE_VAPID_PUBLIC_KEY) {
    return import.meta.env.VITE_VAPID_PUBLIC_KEY;
  }

  if (!supabase) {
    throw new Error('Supabase client is not configured.');
  }

  const { data, error } = await supabase.functions.invoke('push-notifications', {
    body: { action: 'get_vapid_public_key' },
  });

  if (error) {
    throw error;
  }

  if (!data?.vapidPublicKey) {
    throw new Error('Missing VAPID public key.');
  }

  return data.vapidPublicKey as string;
}

export async function subscribeToPushNotifications(input?: { userId?: string | null; installed?: boolean }) {
  const deviceId = getOrCreatePwaDeviceId();
  const permission = await requestNotificationPermission();

  if (permission !== 'granted') {
    return {
      permission,
      subscription: null,
      deviceId,
    } satisfies PushSubscriptionResult;
  }

  if (!supabase) {
    throw new Error('Supabase client is not configured.');
  }

  const registration = await navigator.serviceWorker.ready;
  const vapidPublicKey = await getVapidPublicKey();
  let subscription = await registration.pushManager.getSubscription();

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });
  }

  const { error } = await supabase.functions.invoke('push-notifications', {
    body: {
      action: 'register_subscription',
      deviceId,
      userId: input?.userId || null,
      installed: Boolean(input?.installed),
      notificationPermission: permission,
      userAgent: navigator.userAgent,
      subscription: subscription.toJSON(),
    },
  });

  if (error) {
    throw error;
  }

  return {
    permission,
    subscription,
    deviceId,
  } satisfies PushSubscriptionResult;
}

export async function unsubscribeFromPushNotifications() {
  if (!supabase || !('serviceWorker' in navigator)) {
    return;
  }

  const deviceId = getOrCreatePwaDeviceId();
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();

  if (!subscription) {
    return;
  }

  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();

  const { error } = await supabase.functions.invoke('push-notifications', {
    body: {
      action: 'unregister_subscription',
      deviceId,
      endpoint,
    },
  });

  if (error) {
    throw error;
  }
}

export async function sendSupabasePushNotification(input: {
  title: string;
  body: string;
  url?: string;
  tag?: string;
  deviceId?: string;
  userId?: string | null;
  audience?: 'admins';
  action?: 'send_test_notification' | 'send_notification' | 'send_order_notification' | 'send_admin_order_notification';
}) {
  if (!supabase) {
    throw new Error('Supabase client is not configured.');
  }

  const { error, data } = await supabase.functions.invoke('push-notifications', {
    body: {
      action: input.action || 'send_notification',
      deviceId: input.deviceId || getOrCreatePwaDeviceId(),
      userId: input.userId || null,
      audience: input.audience || null,
      title: input.title,
      body: input.body,
      url: input.url || '/',
      tag: input.tag || 'karebe-notification',
    },
  });

  if (error) {
    throw error;
  }

  return data;
}

export async function showPwaNotification(title: string, options?: { body?: string; url?: string; tag?: string }) {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    throw new Error('Notifications are not enabled.');
  }

  const payload = {
    title,
    body: options?.body,
    url: options?.url,
    tag: options?.tag,
  };

  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.ready;
    await registration.showNotification(title, {
      body: options?.body,
      icon: '/pwa-192.png',
      badge: '/pwa-192.png',
      tag: options?.tag || 'karebe-notification',
      data: { url: options?.url || '/' },
    });

    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'SHOW_NOTIFICATION', payload });
    }
    return;
  }

  new Notification(title, {
    body: options?.body,
    icon: '/pwa-192.png',
    tag: options?.tag || 'karebe-notification',
  });
}
